<template>
  <Layout title="Driver Jobs" description="View your current, next, and previous jobs">
    <div class="px-4 sm:px-0">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">My Jobs</h1>
          <p class="mt-2 text-gray-600">View and manage your assigned bookings</p>
        </div>
        <div class="flex flex-col sm:flex-row gap-2 mt-4 sm:mt-0">
          <input v-model="selectedDate" type="date" class="form-control" @input="fetchDriverJobs" />
        </div>
      </div>

      <!-- 1️⃣ Current Job -->
      <div class="mb-10">
        <h3 class="text-xl font-semibold text-green-700 mb-3">Current Job</h3>
        <div v-if="currentJob" class="grid gap-6">
          <div
            v-for="(b, idx) in [currentJob]"
            :key="'current-' + idx"
            class="card bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
          >
            <div v-html="renderJobCard(b)"></div>
          </div>
        </div>
        <p v-else class="text-gray-500 text-sm">No active job assigned.</p>
      </div>

      <!-- 2️⃣ Next Available Job -->
      <div class="mb-10">
        <h3 class="text-xl font-semibold text-amber-700 mb-3">Next Available Job</h3>
        <div v-if="nextAvailableJob" class="grid gap-6">
          <div
            v-for="(b, idx) in [nextAvailableJob]"
            :key="'next-' + idx"
            class="card bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
          >
            <div v-html="renderJobCard(b)"></div>
          </div>
        </div>
        <p v-else class="text-gray-500 text-sm">No unassigned jobs for this date.</p>
      </div>

      <!-- 3️⃣ Previous Jobs -->
      <div>
        <h3 class="text-xl font-semibold text-gray-700 mb-3">Previous Jobs</h3>
        <div v-if="previousJobs.length" class="grid gap-6">
          <div
            v-for="(b, idx) in previousJobs"
            :key="'prev-' + idx"
            class="card bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
          >
            <div v-html="renderJobCard(b)"></div>
          </div>
        </div>
        <p v-else class="text-gray-500 text-sm">No previous jobs found.</p>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import axios from 'axios'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { useToast } from 'vue-toastification'

export default {
  name: 'DriverJobs',
  components: { Layout },
  data() {
    return {
      selectedDate: '',
      currentJob: null,
      nextAvailableJob: null,
      previousJobs: []
    }
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  methods: {
    async getAuthHeader() {
      const user = getAuth().currentUser
      if (!user) throw new Error('Not logged in')
      const token = await user.getIdToken()
      return { Authorization: token }
    },
    async checkAuthState() {
      const auth = getAuth()
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, function(user) {
          if (user) resolve()
          else reject('No user logged in')
        })
      })
    },
    async fetchDriverJobs() {
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.get(this.$apiURL + '/api/driver_jobs', {
          headers: headers,
          params: { date: this.selectedDate }
        })
        const data = res.data || {}
        this.currentJob = data.currentJob || null
        this.nextAvailableJob = data.nextAvailableJob || null
        this.previousJobs = data.previousJobs || []
      } catch (err) {
        console.error('❌ fetchDriverJobs error:', err)
        this.toast.error('Failed to load jobs.')
      }
    },

    // === Helper Methods ===
    getDisplayStatus(status) {
      if (!status) return '—'
      if (status.toLowerCase() === 'failure') return 'Cancelled'
      if (status.toLowerCase() === 'dop') return 'Completed'
      return status
    },
    getStatusColor(status) {
      const lower = (status || '').toLowerCase()
      if (lower === 'new') return 'bg-blue-100 text-blue-800'
      if (lower === 'assigned') return 'bg-indigo-100 text-indigo-800'
      if (lower === 'ongoing' || lower === 'on route') return 'bg-yellow-100 text-yellow-800'
      if (lower === 'completed' || lower === 'dop') return 'bg-green-100 text-green-800'
      if (lower === 'cancelled' || lower === 'failure') return 'bg-red-100 text-red-800'
      return 'bg-gray-100 text-gray-800'
    },
    formatDT(iso) {
      if (!iso) return '—'
      try {
        var d = new Date(iso)
        return d.toLocaleString('en-SG', { hour12: false })
      } catch (e) {
        return iso
      }
    },
    displayFlightNumbers(fns) {
      try {
        if (Array.isArray(fns)) {
          var list = []
          for (var i = 0; i < fns.length; i++) {
            var o = fns[i]
            if (o && o.number) list.push(o.number)
          }
          return list.join(', ')
        }
        return fns || ''
      } catch (e) {
        return ''
      }
    },

    // === Render Full Job Card ===
    renderJobCard(b) {
      const flights = this.displayFlightNumbers(b.flightNumbers)
      const status = this.getDisplayStatus(b.status)
      const color = this.getStatusColor(b.status)
      const start = this.formatDT(b.startTime)

      let html = `
        <div>
          <div class="flex justify-between items-center px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div>
              <h2 class="text-lg font-semibold text-gray-900">
                ${b.id} — ${b.vesselName || 'No Vessel'} (${b.counterNumber || 'No Counter'})
              </h2>
              <p class="text-sm text-gray-600">${status} • ${start}</p>
              <!----<p class="text-sm text-indigo-600 font-medium">Agent: ${b.clientId || '—'}</p>---->
              <!----<p class="text-sm text-gray-600">Flight: ${flights || '—'}</p>--->
            </div>
            <span class="badge ${color}">${status}</span>
          </div>

          <div class="px-6 py-4 bg-white">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <h4 class="font-medium text-gray-900 mb-1">Start Time</h4>
                <p class="text-sm text-gray-600">${start}</p>
              </div>
              <div>
                <h4 class="font-medium text-gray-900 mb-1">Pax</h4>
                <p class="text-sm text-gray-600">${b.pax || '—'}</p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <h4 class="font-medium text-gray-900 mb-1">Start Location</h4>
                <p class="text-sm text-gray-600">${b.rawStartLocation || '—'}</p>
              </div>
              <div>
                <h4 class="font-medium text-gray-900 mb-1">End Location</h4>
                <p class="text-sm text-gray-600">${b.rawEndLocation || '—'}</p>
              </div>
            </div>
      `

      // Trips
      if (b.trips && b.trips.length) {
        html += `
          <div class="mb-4">
            <h4 class="font-medium text-gray-900 mb-1">Trips</h4>
            <ul class="list-disc pl-5 text-sm text-gray-600">
              ${b.trips.map(function(trip) { return '<li>' + trip + '</li>' }).join('')}
            </ul>
          </div>
        `
      }

      // Remarks
      html += `
        <div class="mb-4">
          <h4 class="font-medium text-gray-900 mb-1">Remarks</h4>
          <p class="text-sm text-gray-600 whitespace-pre-wrap">${b.remarks || '—'}</p>
        </div>
      `

      // Documents
      if (b.documents && b.documents.length) {
        html += `
          <div class="mb-4">
            <h4 class="font-medium text-gray-900 mb-2">Documents</h4>
            ${b.documents.map(function(doc) {
              return `
                <div class="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                  <div class="text-gray-800 font-medium">
                    <i class="bi bi-paperclip text-gray-500 me-1"></i>${doc.fileName || 'Document'}
                    <span class="text-gray-400 text-sm ms-2">
                      ${doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : ''}
                    </span>
                  </div>
                </div>
              `
            }).join('')}
          </div>
        `
      }

      // Timeline
      if (b.statusTimeline && b.statusTimeline.length) {
        html += `
          <div class="col-12 mt-4">
            <label class="form-label fw-semibold mb-1">Status Timeline</label>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              ${b.statusTimeline.map(function(s) {
                return `
                  <div class="border rounded p-3 bg-gray-50 shadow-sm">
                    <label class="block text-sm font-medium text-gray-700 mb-1">${s.status}</label>
                    <p class="text-sm text-gray-600">${s.time ? new Date(s.time).toLocaleString() : '—'}</p>
                  </div>
                `
              }).join('')}
            </div>
          </div>
        `
      }

      html += `</div></div>`
      return html
    }
  },
  async mounted() {
    await this.checkAuthState()
    const today = new Date()
    this.selectedDate = today.toISOString().split('T')[0]
    await this.fetchDriverJobs()
  }
}
</script>

<style scoped>
.badge {
  display: inline-block;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
}
</style>
